import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import type { CopyScheduleItem, JobItem, KwikstageJobItem, SaveItem } from './models/JobScheduleInterfaces';
import { GetQuery, PostQuery } from './Shared';

interface CopyJobProps {
    onOffhireDate: string;
    copyItem: CopyScheduleItem;
    scheduleBranchId: number;
    jobs: JobItem[];
    kwikstageJobs: KwikstageJobItem[];
    showModal: boolean;
    hideModal(): void;
    updateOnOffHireDate(onOffhireDate: string): void;
    updateJobs(jobs: JobItem[]): void;
    updateKwikstageJobs(kwikstageJobs: KwikstageJobItem[]): void;
    saveModal(saveItem: SaveItem): void;
}

interface CopyJobModal {
    extraDays: number;
    quantityUsed: number;
    showWarning: boolean;
    errors: any;
    loading: boolean;
}

export default class CopyJobData extends React.Component<CopyJobProps, CopyJobModal> {
    constructor(props: CopyJobProps) {
        super(props);

        this.state = {
            extraDays: 1,
            quantityUsed: 0,
            showWarning: false,
            errors: {},
            loading: false
        }
    }

    resetCopy = () => {
        this.setState({
            extraDays: 1, quantityUsed: 0, errors: {}, loading: false
        });
    }

    hideModal = () => {
        this.resetCopy();
        this.props.hideModal();
    }

    handleChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
            delete errors["jobs"];
        }

        if (e.target.name === "extraDays") {
            //need to add or remove a line from the grid
            if (!this.props.copyItem.isKwikstage) {
                var items = this.props.jobs;
                if (items.length < e.target.value) {
                    //add extra item
                    let jobItem: JobItem = {
                        itemIndex: 0,
                        quantity: 0,
                        scheduleDate: "",
                        warning: false,
                        locked: false
                    };
                    items.push(jobItem);
                } else {
                    //remove a row
                    items.pop();
                }
                this.props.updateJobs(items);
            }
        }

        let change: any = {};
        change[e.target.name] = e.target.value;
        change["errors"] = errors;
        this.setState(change);
    }

    handlePropsChange = (e: any) => {
        e.preventDefault();
        this.props.updateOnOffHireDate(e.target.value);
    }

    //grid item changed
    onCellChange = (e: any, index: number) => {
        //update item that has changed
        var jobs = this.props.jobs;
        var job = jobs[index];
        let newItem: any = {};
        newItem = job;
        newItem[e.target.name] = e.target.value;

        var totalQty = 0;
        for(var x = 0; x < jobs.length; x++) {
            totalQty += jobs[x].quantity * 1;
        }

        if (e.target.name === "scheduleDate") {
            var blankKsJob: KwikstageJobItem = {
                jobQuoteItemCalculationId: 0,
                liftId: 0,
                starterBayId: 0,
                baysTotal: 0,
                maxBays: 0,
                bays: 0,
                scheduleDate: '',
                locked: false,
                warning: false
            };
            //check to see if the date is locked or a public holiday or within a shutdown period
            this.checkDateLockedWarning(false, jobs, job, [], blankKsJob, totalQty, e.target.value);
        } else {
            this.setState({ quantityUsed: totalQty, errors: {} });
            this.props.updateJobs(jobs);
        }   
    }

    async checkDateLockedWarning(isKwikstage: boolean, jobs: JobItem[], job: JobItem, ksJobs: KwikstageJobItem[], ksJob: KwikstageJobItem, totalQty: number, scheduleDate: string) {
        const apiUrl = "CheckDateLockedWarning?ScheduleDate=" + scheduleDate + '&ScheduleBranchId=' + this.props.scheduleBranchId;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            var locked = result.data.locked;
            var holiday = result.data.warning;

            if (isKwikstage) {
                if (locked) {
                    ksJob.locked = true;
                    ksJob.warning = false;
                    toast.error("The date you have selected has been locked, please select a new date or unlock before rescheduling!");
                } else if (holiday) {
                    ksJob.locked = false;
                    ksJob.warning = true;
                } else {
                    ksJob.locked = false;
                    ksJob.warning = false;
                };
                this.setState({ errors: {} });
                this.props.updateKwikstageJobs(ksJobs);
            } else {
                if (locked) {
                    job.locked = true;
                    job.warning = false;
                    toast.error("The date you have selected has been locked, please select a new date or unlock before rescheduling!");
                } else if (holiday) {
                    job.locked = false;
                    job.warning = true;
                } else {
                    job.locked = false;
                    job.warning = false;
                };
                this.setState({ quantityUsed: totalQty, errors: {} });
                this.props.updateJobs(jobs);
            }
        } 
    }

    onCellChangeKwikstage = (e: any, index: number) => {
        //update item that has changed
        var ksJobs = this.props.kwikstageJobs;
        var ksJob = ksJobs[index];
        let newItem: any = {};
        newItem = ksJob;
        newItem[e.target.name] = e.target.value;

        var totalQty = 0;
        for (var x = 0; x < ksJobs.length; x++) {
            totalQty += ksJobs[x].bays * 1;
        }

        if (e.target.name === "scheduleDate") {
            let blankJobItem: JobItem = {
                itemIndex: 0,
                quantity: 0,
                scheduleDate: "",
                warning: false,
                locked: false
            };
            this.checkDateLockedWarning(true, [], blankJobItem, ksJobs, ksJob, totalQty, e.target.value);
        } else {
            this.setState({ quantityUsed: totalQty, errors: {} });
            this.props.updateKwikstageJobs(ksJobs);
        }
    }

    validate = () => {
        let errors: any = {};
        let formIsValid = true;
        var jobs = this.props.jobs;

        //check if any dates are locked before validating
        var locked = jobs.filter(i => i.locked);
        if (locked.length > 0) {
            toast.error("You must select dates that are not locked in order to rollover to!");
            return false;
        }

        
        if (jobs.length === 0) {
            toast.error("Please add at least one job to Schedule");
            return false;
        }

        if (!this.props.onOffhireDate) {
            errors["onOffhireDate"] = "On/Off Hire Date is required";
            formIsValid = false;
        } else if (!DateHelpers.isValidDate(this.props.onOffhireDate)) {
            errors["onOffhireDate"] = "On/Off Hire Date is required";
            formIsValid = false;
        }

        var errorString = "Please enter the following values: ";
        var error = false;

        var totalQty = 0;
        for (var i = 0; i < jobs.length; i++) {
            //rowNumber += 1;
            var newErrorString = "";

            if (!jobs[i].scheduleDate) {
                newErrorString += "Schedule Date, ";
                error = true;
            } else if (!DateHelpers.isValidDate(jobs[i].scheduleDate)) {
                newErrorString += " Valid Schedule Date, ";
                error = true;
            }

            //need to validate on/off hire date
            //if (jobs[i].typeId === 1 && !DateHelpers.compareDate(jobs[i].scheduleDate, this.props.onOffhireDate)) {
            //    newErrorString += " Onhire Date >= Schedule Date, ";
            //    error = true;
            //}
            //if (jobs[i].typeId === 2 && !DateHelpers.compareDate(this.props.onOffhireDate, jobs[i].scheduleDate)) {
            //    newErrorString += " Offhire Date <= Schedule Date, ";
            //    error = true;
            //}

            if (jobs[i].quantity <= 0) {
                newErrorString += " Quantity > 0, ";
                error = true;
            } else {
                totalQty += jobs[i].quantity * 1;
            }

            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + (i + 1) + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }

        }

        if ((this.props.copyItem.quantityScheduled * 1) - totalQty < 0) {
            errorString += "Total quantity can't exceed the total scheduled quantity of " + this.props.copyItem.quantityScheduled;
            error = true;
        }

        if (error) {
            formIsValid = false;
            errors["jobs"] = errorString;
        }
        this.setState({ errors: errors });
        return formIsValid;
    }

    validateKwikstage = () => {
        let errors: any = {};
        let formIsValid = true;
        var kwikstageJobs = this.props.kwikstageJobs;

        //check if any dates are locked before validating
        var locked = kwikstageJobs.filter(i => i.locked);
        if (locked.length > 0) {
            toast.error("You must select dates that are not locked in order to rollover to!");
            return false;
        }


        if (kwikstageJobs.length === 0) {
            toast.error("Please add at least one job to Schedule");
            return false;
        }

        if (!this.props.onOffhireDate) {
            errors["onOffhireDate"] = "On/Off Hire Date is required";
            formIsValid = true;
        } else if (!DateHelpers.isValidDate(this.props.onOffhireDate)) {
            errors["onOffhireDate"] = "On/Off Hire Date is required";
            formIsValid = true;
        }

        var errorString = "Please enter the following values: ";
        var error = false;

        var totalQty = 0;
        for (var i = 0; i < kwikstageJobs.length; i++) {
            //rowNumber += 1;
            var newErrorString = "";

            var qty = kwikstageJobs[i].bays * 1;

            if (qty > 0) {
                if (!kwikstageJobs[i].scheduleDate) {
                    newErrorString += "Schedule Date, ";
                    error = true;
                } else if (!DateHelpers.isValidDate(kwikstageJobs[i].scheduleDate)) {
                    newErrorString += " Valid Schedule Date, ";
                    error = true;
                }

                if (qty > kwikstageJobs[i].maxBays) {
                    newErrorString += "Quantity can't exceed existing quantity of " + kwikstageJobs[i].maxBays + ", ";
                    error = true;
                }
            }

            totalQty += kwikstageJobs[i].bays * 1;

            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + (i + 1) + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }
        }

        if (totalQty === 0) {
            errorString += "At least one item must have a Quantity > 0";
            error = true;
        }

        if ((this.props.copyItem.quantityScheduled * 1) - totalQty < 0) {
            errorString += "Total quantity can't exceed the total scheduled quantity of " + this.props.copyItem.quantityScheduled;
            error = true;
        }

        if (error) {
            formIsValid = false;
            errors["jobs"] = errorString;
        }
        this.setState({ errors: errors });
        return formIsValid;
    }

    schedule = (e: any) => {
        e.preventDefault();
        if (this.props.copyItem.isKwikstage) {
            if (this.validateKwikstage()) {
                this.saveKwikstageItems();
            }
        } else {
            if (this.validate()) {
                this.saveCopyItems();
            }
        }
    }

    saveKwikstageItems = async () => {
        var jobItems = this.props.kwikstageJobs.filter(j => j.bays > 0);

        var copyKSjob = {
            scheduleId: this.props.copyItem.scheduleId,
            onOffHireDate: this.props.onOffhireDate,
            item: this.props.copyItem.item,
            jobItems: jobItems
        };
        var jsonItem = JSON.stringify(copyKSjob);

        const apiUrl = "SaveCopyKwikstageJobItems";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving KS items!");
        } else {
            toast.success("New Schedule/s Saved");
            var dates: string[] = [];
            for (var x = 0; x < this.props.kwikstageJobs.length; x++) {
                dates.push(this.props.kwikstageJobs[x].scheduleDate);
            }

            var saveItem: SaveItem = {
                onOffhireDate: this.props.onOffhireDate,
                quantity: (this.props.copyItem.quantityScheduled * 1) - (this.state.quantityUsed * 1),
                dates: dates
            }
            this.resetCopy();
            this.props.saveModal(saveItem);
        }
    }

    saveCopyItems = async () => {
        var copyjob = {
            scheduleId: this.props.copyItem.scheduleId,
            onOffHireDate: this.props.onOffhireDate,
            item: this.props.copyItem.item,
            jobItems: this.props.jobs
        };
        var jsonItem = JSON.stringify(copyjob);

        const apiUrl = "SaveCopyJobItems";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error copying job!");
        } else {
            toast.success("New Schedule/s Saved");
            var dates: string[] = [];
            for (var x = 0; x < this.props.jobs.length; x++) {
                dates.push(this.props.jobs[x].scheduleDate);
            }

            var saveItem: SaveItem = {
                onOffhireDate: this.props.onOffhireDate,
                quantity: (this.props.copyItem.quantityScheduled * 1) - (this.state.quantityUsed * 1),
                dates: dates
            }
            this.resetCopy();
            this.props.saveModal(saveItem);
        }
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();

        return (<div>
            {contents}
        </div>);
    }

    renderDetails = () => {
        let remainingQty = (this.props.copyItem.quantityScheduled * 1) - (this.state.quantityUsed * 1);
        let warnings = this.props.jobs.filter(r => r.warning || r.locked);
        let starterBayOptions = this.props.copyItem.kwikstageOptions;

        return (
            <div className="static-modal">
                <Modal className="sizemd" show={this.props.showModal} onHide={this.hideModal}>
                    <Modal.Header className="modal__header--colour">
                        <Modal.Title>Rollover - {this.props.copyItem.item} {this.props.copyItem.qtyType}</Modal.Title>
                        <button type="button" className="close" onClick={this.hideModal}>&times;</button>
                    </Modal.Header>
                    <Modal.Body>
                        <div>{this.props.copyItem.siteAddress}</div>

                        <label className="input-group" htmlFor="quantityScheduled">
                            <span className="label">Quantity </span>
                            <input className="input" type="number" id="quantityScheduled" name="quantityScheduled" value={this.props.copyItem.quantityScheduled} disabled></input>
                        </label>
                        <label className="input-group" htmlFor="extraDays">
                            <span className="label">Extra Days </span>
                            <input className="input allocatenewJobId" min={1} type="number" id="extraDays" name="extraDays" value={this.state.extraDays} onChange={(e) => this.handleChange(e)} disabled={this.props.copyItem.isKwikstage}></input>
                        </label>
                        <label className="input-group" htmlFor="onOffhireDate">
                            <span className="label">On/Off Hire Date </span>
                            <input className="input" type="date" id="onOffhireDate" name="onOffhireDate" value={this.props.onOffhireDate} onChange={(e) => this.handlePropsChange(e)}></input>
                        </label>
                        <span className="label errors">{this.state.errors["onOffhireDate"]}</span>

                        <div className={this.props.copyItem.isKwikstage ? "" : "hidden"}>
                            Existing Kwikstage Bays
                            {this.props.copyItem.kwikstageItems.map((item, index) =>
                                <div key={index}>
                                    <label className="label rollover-padding">
                                        {item.starterBay}
                                    </label>
                                    <label className="label rollover-padding">
                                        {item.lift}
                                    </label>
                                    <label className="label rollover-padding">
                                        {item.bays} bays
                                    </label>
                                </div>
                            )}
                        </div>

                        Enter dates and quantities for rollover items:
                        <div className='overflowAuto'>
                            <table className={this.props.copyItem.isKwikstage ? "hidden" : 'table table__small tableColours'}>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Schedule Date</th>
                                        <th>Quantity</th>
                                        <th>Type</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.props.jobs.map((job, index) =>
                                        <tr key={index}>
                                            <td className="table__text--align">{index + 1}</td>
                                            <td className="table__text--align"><input type='date' id="scheduleDate" name="scheduleDate" className="form-control" value={job.scheduleDate} onChange={(e) => this.onCellChange(e, index)} /></td>
                                            <td className="table__text--align">
                                                <input type='number' id="quantity" name="quantity" className="input schedulenew__text-sm" value={job.quantity} onChange={(e) => this.onCellChange(e, index)} />
                                            </td>
                                            <td className="table__text--align">{this.props.copyItem.unitOfMeasure}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            <table className={this.props.copyItem.isKwikstage ? 'table table__small tableColours' : "hidden"}>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Schedule Date</th>
                                        <th>Bay</th>
                                        <th>Lift</th>
                                        <th>Qty</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.props.kwikstageJobs.map((job, index) =>
                                        <tr key={index}>
                                            <td className="table__text--align">{index + 1}</td>
                                            <td className="table__text--align"><input type='date' id="scheduleDate" name="scheduleDate" className="form-control" value={job.scheduleDate} onChange={(e) => this.onCellChangeKwikstage(e, index)} /></td>
                                            <td className="table__text--align">
                                                <select className="select table__select--size" id="starterBayId" name="starterBayId" value={job.starterBayId} disabled>
                                                    <option hidden defaultValue="-1"></option>
                                                    {starterBayOptions.map(item =>
                                                        <option key={item.starterBayId} value={item.starterBayId}>{item.starterBay}</option>
                                                    )};
                                                </select>
                                            </td>
                                            <td className="table__text--align">
                                                <select className="select table__select--size" id="liftId" name="liftId" value={job.liftId} disabled>
                                                    <option hidden defaultValue="-1"></option>
                                                    {starterBayOptions.find(s => s.starterBayId === (job.starterBayId * 1))?.liftOptions.map(item =>
                                                        <option key={item.liftId} value={item.liftId}>{item.lift}</option>
                                                    )};
                                                </select>
                                            </td>
                                            <td className="table__text--align">
                                                <input type='number' id="bays" name="bays" className="input schedulenew__text-sm" value={job.bays} onChange={(e) => this.onCellChangeKwikstage(e, index)} />
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            {this.state.errors["jobs"] ?
                                (this.state.errors["jobs"]).split("\n").map((item: any, key: any) => {
                                    return <span className="label errors" key={key}>{item}<br /></span>
                                })
                                : ""}
                            {warnings.map((item, index) =>
                                <span className="label errors" key={index}>{'Warning: ' + DateHelpers.formatDate(item.scheduleDate) + ' is ' + (item.locked ? ' locked. Please unlock day or selected another date' : ' a public holiday or within a shutdown period')}<br /></span>
                            )}
                        </div>
                        <span className={this.state.showWarning ? "warning" : "hidden"}>Warning: one or more dates selected are Public Holidays or within a Shutdown Period!</span><br />
                        <span className={this.props.copyItem.isKwikstage ? "" : "hidden"}>Original Item Scheduled Quantity will be updated to {remainingQty}</span><br />
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="popup-button--yes" onClick={this.schedule}>Schedule</button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}